import { useMemo, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Box, Button, CircularProgress, Stack, Typography } from '@mui/material'
import IconButton from '@mui/material/IconButton'
import PlannedExpertiseBottomBar from './PlannedExpertiseBottomBar'
import useStyles, { workflowTunnelSx } from './_styles'
import { LogoutRoundedIcon, HelpOutlinedIcon } from '../../../assets/icons/icons'
import PlannedExpertiseAbandonDialog from '../plannedExpertiseEditor/PlannedExpertiseAbandonDialog'
import PlannedExpertiseDraftDialog from './PlannedExpertiseDraftDialog'
import PlannedExpertiseProgressBar from './PlannedExpertiseProgressBar'
import PlannedExpertiseWorkflowContent from './PlannedExpertiseWorkflowContent'
import PlannedExpertiseWorkflowRecap from './PlannedExpertiseWorkflowRecap'
import PlannedExpertiseTransmission from './PlannedExpertiseTransmission'
import {
    useGetIdsHealthcareservicesHcsIdTransmissionsTransmissionId,
    usePutIdsHealthcareservicesHcsIdTransmissionsTransmissionIdProgression,
} from '@toktokdoctech/tokapi-client'
import useCurrentHcs from '../../../hooks/useCurrentHcs'
import { getSkipDisplay, getCanGoNext, parseTransmissionRequest } from '../../utilsComponents/workflowParser'
import WorkflowPageShell from './workflow/WorkflowPageShell'
import { useWorkflowQuestions } from './workflow/hooks/useWorkflowQuestions'
import { useWorkflowNavigation } from './workflow/hooks/useWorkflowNavigation'
import { useTransmissionSender } from './workflow/hooks/useTransmissionSender'
import { useWorkflowRecap } from './workflow/hooks/useWorkflowRecap'
import { useQuestionTypeFlags } from './workflow/hooks/useQuestionTypeFlags'
import { buildTransmissionPayload } from './workflow/utils/workflowPlayload'
import { clearPersistedProgress } from './workflow/utils/workflowStorage'
import { HelpCenter } from 'libs/frontend-kit/src/lib/components/feature/helpCenter/HelpCenter'
import TokTokDocSnackbar from '../../Common/Snackbar'

/**
  Main page of the workflow “tunnel” for completing a
  remote assessment request: displays questions one by one, handles
  forward/back navigation, saves progress, and sends the
  final submission once all questions have been answered.

  All business logic is handled in the hooks within the `workflow/` folder:
  - useWorkflowQuestions   : parsing the workflow JSON
  - useWorkflowNavigation  : current position, answers, next/back
  - useTransmissionSender  : final submission + progress bar
  - useWorkflowRecap       : summary information displayed at the bottom of the page
  - useQuestionTypeFlags   : question type booleans for display
  This component now only assembles them and handles the rendering.
 */
export default function PlannedExpertiseWorkflowTunnelPage() {
    const classes = useStyles()
    const navigate = useNavigate()
    const { transmissionId } = useParams<{ transmissionId?: string }>()
    const currentHcs = useCurrentHcs()
    const [isHelpOpen, setIsHelpOpen] = useState(false)
    const [isAbandonDialogOpen, setIsAbandonDialogOpen] = useState(false)
    const [isDraftDialogOpen, setIsDraftDialogOpen] = useState(false)
    const { mutateAsync: transmitProgression } =
        usePutIdsHealthcareservicesHcsIdTransmissionsTransmissionIdProgression()

    const {
        data: transmissionDetail,
        isFetching: isTransmissionDetailFetching,
        error: transmissionDetailError,
    } = useGetIdsHealthcareservicesHcsIdTransmissionsTransmissionId(currentHcs.id, transmissionId ?? '', {
        query: { enabled: Boolean(transmissionId), refetchOnWindowFocus: false },
    })

    const workflowJsonContent = transmissionDetail?.workflow?.jsonContent ?? null
    const { workflowQuestions, rawByIdMap } = useWorkflowQuestions(workflowJsonContent)
    const requestAnswers = useMemo(
        () => parseTransmissionRequest(transmissionDetail?.request as any[], transmissionDetail as any).answers,
        [transmissionDetail],
    )

    const saveProgress = async (answersMap: Map<string, any[]>, positionId: string | null) => {
        if (!transmissionId) return
        const payload = buildTransmissionPayload(answersMap, positionId)
        await transmitProgression({ hcsId: currentHcs.id, transmissionId, data: payload })
    }

    const {
        status: transmissionStatus,
        progress: transmissionProgress,
        sendTransmission,
    } = useTransmissionSender(currentHcs.id, transmissionId, transmitProgression, rawByIdMap, workflowQuestions)

    const {
        currentQuestion,
        currentQuestionId,
        isRecap,
        visitedQuestionIds,
        updateRecapAnswer,
        handleRecapTransmission,
        canTransmitRecap,
        committedAnswersByQuestionId,
        progress,
        localAnswersByQuestionId,
        setLocalAnswersByQuestionId,
        handleNext,
        handleSelectChoice,
        handleSkip,
        handleBack,
        showBack,
        canGoBack,
        isNavigating,
    } = useWorkflowNavigation(
        workflowQuestions,
        rawByIdMap,
        workflowJsonContent,
        transmissionDetail,
        isTransmissionDetailFetching,
        transmissionId,
        requestAnswers,
        saveProgress,
        sendTransmission,
    )

    const effectiveRecap = useWorkflowRecap(transmissionDetail)

    const {
        isTextFreeQuestion,
        isNumberFreeQuestion,
        isSliderQuestion,
        isDateQuestion,
        isSketchQuestion,
        isChoiceQuestion,
        isMultipleChoiceQuestion,
        isMessageQuestion,
    } = useQuestionTypeFlags(currentQuestion)

    const currentPatientSex =
        (transmissionDetail?.patient as any)?.sexe ?? (transmissionDetail?.patient as any)?.gender ?? null

    const { showSkip, skipReplacesNext } = getSkipDisplay(
        currentQuestion,
        isChoiceQuestion,
        isMultipleChoiceQuestion,
        localAnswersByQuestionId,
    )
    const canGoNext = getCanGoNext(
        currentQuestion,
        isTextFreeQuestion,
        isNumberFreeQuestion,
        isMultipleChoiceQuestion,
        requestAnswers,
        localAnswersByQuestionId,
    )

    const handleHelpClick = () => setIsHelpOpen((prev) => !prev)
    const handleAbandon = () => setIsAbandonDialogOpen(true)
    const handleDraft = () => setIsDraftDialogOpen(true)
    const isDetailLoading = Boolean(transmissionId) && isTransmissionDetailFetching
    const isTransmissionActive = transmissionStatus !== 'idle'
    const isTransmissionFailed = transmissionStatus === 'error' || transmissionStatus === 'warning'

    if (transmissionId && transmissionDetailError) {
        return (
            <WorkflowPageShell>
                <Typography color="error" fontWeight={600}>
                    Impossible de récupérer les détails de la téléexpertise.
                </Typography>
            </WorkflowPageShell>
        )
    }

    if (isDetailLoading) {
        return (
            <WorkflowPageShell>
                <Box className={classes.progressWrapper}>
                    <CircularProgress size="3.6rem" />
                    <Typography fontWeight={600} fontSize="1.6rem" sx={{ mt: 2 }}>
                        Chargement du détail de la téléexpertise
                    </Typography>
                </Box>
            </WorkflowPageShell>
        )
    }

    return (
        <Stack sx={workflowTunnelSx.root}>
            <Stack sx={workflowTunnelSx.workflowNav}>
                <Typography className={classes.title}>
                    {isTransmissionActive
                        ? 'Transmettre la demande de téléexpertise'
                        : isRecap
                        ? 'Récapitulatif avant transmission'
                        : 'Compléter la demande de téléxpertise'}
                </Typography>
                <Box sx={workflowTunnelSx.actions}>
                    <IconButton onClick={handleHelpClick}>
                        <HelpOutlinedIcon className={classes.HeaderRightHelpButton} />
                    </IconButton>
                    {!isTransmissionActive && (
                        <Button onClick={handleAbandon} variant="text" sx={workflowTunnelSx.abandonButton}>
                            Abandonner
                        </Button>
                    )}
                    <PlannedExpertiseAbandonDialog
                        isOpen={isAbandonDialogOpen}
                        onClose={() => setIsAbandonDialogOpen(false)}
                        onConfirm={() => {
                            if (transmissionId) clearPersistedProgress(transmissionId)
                            navigate('/teleexpertises')
                        }}
                        transmissionId={transmissionId}
                        mode="workflow"
                    />
                    <Button
                        onClick={handleDraft}
                        disabled={isNavigating || (isTransmissionActive && !isTransmissionFailed)}
                        variant="text"
                        sx={workflowTunnelSx.resumeButton}
                    >
                        <LogoutRoundedIcon />
                        Terminer plus tard
                    </Button>
                    <PlannedExpertiseDraftDialog
                        isOpen={isDraftDialogOpen}
                        onClose={() => setIsDraftDialogOpen(false)}
                        onConfirm={() => navigate('/teleexpertises')}
                        transmissionId={transmissionId}
                        committedAnswersByQuestionId={committedAnswersByQuestionId}
                        localAnswersByQuestionId={localAnswersByQuestionId}
                        currentQuestionId={currentQuestionId}
                        isRecap={isRecap}
                        saveProgress={saveProgress}
                    />
                </Box>
            </Stack>
            {isTransmissionActive ? (
                <PlannedExpertiseTransmission
                    status={transmissionStatus}
                    progress={transmissionProgress}
                    onRetry={() => sendTransmission(committedAnswersByQuestionId)}
                />
            ) : (
                <Stack sx={workflowTunnelSx.workflowInnerContainer}>
                    {!isRecap && (
                        <Box className={classes.progressWrapper}>
                            <PlannedExpertiseProgressBar
                                activeStep={progress.currentStep}
                                steps={progress.totalSteps || 6}
                                workflowLabel={effectiveRecap?.workflowLabel}
                            />
                        </Box>
                    )}
                    <Box className={classes.content}>
                        <Box className={classes.questionWrapper}>
                            {isRecap ? (
                                <PlannedExpertiseWorkflowRecap
                                    questions={visitedQuestionIds
                                        .map((questionId) =>
                                            workflowQuestions.find((question) => question.id === questionId),
                                        )
                                        .filter(Boolean)}
                                    answersByQuestionId={committedAnswersByQuestionId}
                                    onAnswer={updateRecapAnswer}
                                />
                            ) : !currentQuestion || progress.totalSteps === 0 ? (
                                <Typography>
                                    {progress.isComplete
                                        ? 'Toutes les questions ont été répondues !'
                                        : 'Aucune question disponible'}
                                </Typography>
                            ) : (
                                <PlannedExpertiseWorkflowContent
                                    currentQuestion={currentQuestion}
                                    progress={progress}
                                    requestAnswers={requestAnswers}
                                    localAnswersByQuestionId={localAnswersByQuestionId}
                                    setLocalAnswersByQuestionId={setLocalAnswersByQuestionId}
                                    isTextFreeQuestion={isTextFreeQuestion}
                                    isNumberFreeQuestion={isNumberFreeQuestion}
                                    isSliderQuestion={isSliderQuestion}
                                    isDateQuestion={isDateQuestion}
                                    isSketchQuestion={isSketchQuestion}
                                    isChoiceQuestion={isChoiceQuestion}
                                    isMultipleChoiceQuestion={isMultipleChoiceQuestion}
                                    isMessageQuestion={isMessageQuestion}
                                    handleSelectChoice={handleSelectChoice}
                                    currentPatientSex={currentPatientSex}
                                />
                            )}
                        </Box>
                    </Box>
                    <PlannedExpertiseBottomBar
                        recap={effectiveRecap}
                        onBack={!isRecap ? handleBack : undefined}
                        showBack={showBack}
                        backDisabled={!canGoBack}
                        backLoading={isNavigating}
                        onNext={isRecap ? handleRecapTransmission : handleNext}
                        onSkip={handleSkip}
                        showSkip={!isRecap && showSkip}
                        skipReplacesNext={skipReplacesNext}
                        skipLoading={isNavigating}
                        canGoNext={isRecap ? canTransmitRecap : canGoNext}
                        goNextLabel={isRecap ? 'Transmettre' : 'Suivant'}
                    />
                </Stack>
            )}
            <HelpCenter open={isHelpOpen} onClose={handleHelpClick} />
            <TokTokDocSnackbar />
        </Stack>
    )
}
